export type Slot = 'full' | 'am' | 'pm';

export type ResourceState =
  | 'free'
  | 'am_free'
  | 'pm_free'
  | 'taken'
  | 'blocked'
  | 'reserved';

export interface Site {
  id: string;
  code: string;
  name: string;
  timezone: string;
  /** Working days ahead people may book. Null means no limit. */
  max_days_ahead: number | null;
  /** Today at the site, not in the browser's timezone. */
  today: string;
  /**
   * Last date an employee may book, or null when the site has no
   * window at all. Admin overrides ignore this either way.
   */
  bookable_until: string | null;
  /** The person's own office, as opposed to one they were granted. */
  is_home: boolean;
  /**
   * The dates a grant covers, when this site is not their own. The site
   * becomes visible as soon as the grant exists so they can look at the
   * floor plan before travelling, but booking outside these dates is
   * refused by the database. Null at their home site.
   */
  granted_from: string | null;
  granted_to: string | null;
}

export interface Area {
  id: string;
  code: string;
  name: string;
  kind: 'office' | 'parking';
  map_key: string | null;
}

export interface AvailableResource {
  id: string;
  label: string;
  zone: string | null;
  tags: string[];
  map: { x: number; y: number } | null;
  state: ResourceState;
  freeSlots: Slot[];
  heldBy: string | null;
  mine: boolean;
  bookable: boolean;
}

export interface AreaSummary {
  capacity: number;
  free: number;
  half_free: number;
  taken: number;
  blocked: number;
}

export interface Availability {
  date: string;
  areaId: string;
  summary: AreaSummary;
  resources: AvailableResource[];
}

/**
 * A desk seen across several days at once.
 *
 * freeDays out of the range's total is the whole point: a desk free on
 * Monday and taken on Wednesday must not look like one free all week.
 */
export interface RangeResource {
  id: string;
  label: string;
  zone: string | null;
  tags: string[];
  map: { x: number; y: number } | null;
  freeDays: number;
  restricted: boolean;
  bookable: boolean;
}

export interface RangeAvailability {
  areaId: string;
  from: string;
  to: string;
  /** How many days the range covers, weekends already excluded. */
  days: number;
  dates: string[];
  resources: RangeResource[];
}

/**
 * Booking a range is deliberately not all-or-nothing. Days that could
 * not be booked come back with a short reason -- usually that the
 * person already holds that day -- and the rest are created.
 */
export interface RangeBookingResult {
  created: {
    id: string;
    resource_id: string;
    local_date: string;
    slot: Slot;
    status: string;
  }[];
  skipped: { date: string; reason: string }[];
}

export interface MyBooking {
  id: string;
  date: string;
  slot: Slot;
  status: string;
  label: string;
  kind: 'desk' | 'parking';
  area_name: string;
  site_code: string;
}

export interface Me {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
}

export const SLOT_LABEL: Record<Slot, string> = {
  full: 'Full day',
  am: '08:00 – 12:00',
  pm: '12:00 – 16:00',
};
